import { useState } from 'react';
import { Bell, Settings, Search, X } from 'lucide-react';
import { NotificationPanel } from '../notifications';

const Header = ({ state, dispatch }) => {
  const [showNotifications, setShowNotifications] = useState(false);
  const [searchFocused, setSearchFocused] = useState(false);
  const [localSearch, setLocalSearch] = useState('');
  const unreadCount = state.notifications.filter(n => !n.read).length;

  const handleSearch = (value) => {
    setLocalSearch(value);
    if (value.length >= 2) {
      // Search customers
      const matchedCustomer = state.customers.find(c =>
        c.name.toLowerCase().includes(value.toLowerCase()) ||
        c.medicareNumber.includes(value) ||
        c.id.toLowerCase().includes(value.toLowerCase())
      );
      if (matchedCustomer) {
        dispatch({ type: 'SELECT_CUSTOMER', payload: matchedCustomer.id });
        dispatch({ type: 'SET_ACTIVE_TAB', payload: 'customer-detail' });
        setLocalSearch('');
        setSearchFocused(false);
      }
    }
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (localSearch.length >= 2) {
      dispatch({ type: 'SET_SEARCH_QUERY', payload: localSearch });
      dispatch({ type: 'SET_ACTIVE_TAB', payload: 'customers' });
      setLocalSearch('');
      setSearchFocused(false);
    }
  };

  // Search suggestions dropdown
  const suggestions = localSearch.length >= 1
    ? state.customers.filter(c =>
        c.name.toLowerCase().includes(localSearch.toLowerCase()) ||
        c.medicareNumber.includes(localSearch)
      ).slice(0, 5)
    : [];

  return (
    <div className="sticky top-0 bg-white/90 backdrop-blur-xl border-b border-gray-200/50 px-4 py-3 z-20 shadow-sm">
      <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
        {/* Left: Logo */}
        <div className="flex items-center space-x-3 flex-shrink-0">
          <img
            src="/blooms-logo.png"
            alt="Blooms The Chemist"
            className="h-8 object-contain"
          />
          <div className="hidden md:block h-6 w-px bg-gray-200" />
          <span className="hidden md:block text-sm font-semibold text-gray-500">MedFast</span>
        </div>

        {/* Center: Global search */}
        <form onSubmit={handleSearchSubmit} className="hidden md:block flex-1 max-w-md relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            value={localSearch}
            onChange={(e) => { setLocalSearch(e.target.value); handleSearch(e.target.value); }}
            onFocus={() => setSearchFocused(true)}
            onBlur={() => setTimeout(() => setSearchFocused(false), 200)}
            placeholder="Search customers, medications..."
            className="w-full pl-10 pr-8 py-2 bg-white/80 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 transition-all outline-none text-sm"
          />
          {localSearch && (
            <button
              type="button"
              onClick={() => setLocalSearch('')}
              className="absolute right-3 top-1/2 -translate-y-1/2"
            >
              <X className="h-4 w-4 text-gray-400" />
            </button>
          )}

          {/* Search suggestions dropdown */}
          {searchFocused && suggestions.length > 0 && (
            <div className="absolute top-full mt-1 w-full bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden z-50">
              {suggestions.map(customer => (
                <button
                  key={customer.id}
                  onMouseDown={() => {
                    dispatch({ type: 'SELECT_CUSTOMER', payload: customer.id });
                    dispatch({ type: 'SET_ACTIVE_TAB', payload: 'customer-detail' });
                    setLocalSearch('');
                  }}
                  className="w-full px-4 py-2.5 text-left hover:bg-brand-50 transition-colors flex items-center justify-between"
                >
                  <div>
                    <p className="text-sm font-medium text-gray-900">{customer.name}</p>
                    <p className="text-xs text-gray-500">{customer.medicareNumber}</p>
                  </div>
                  <span className="text-xs text-gray-400">{customer.prescriptions.filter(rx => rx.status === 'Active').length} active scripts</span>
                </button>
              ))}
            </div>
          )}
        </form>

        {/* Right: Actions */}
        <div className="flex items-center space-x-1">
          {/* Notifications */}
          <div className="relative">
            <button
              onClick={() => setShowNotifications(!showNotifications)}
              className="relative p-2 rounded-xl hover:bg-white/50 transition-all"
              aria-label="Notifications"
            >
              <Bell className="h-5 w-5 text-gray-600" />
              {unreadCount > 0 && (
                <div className="absolute top-0.5 right-0.5 w-5 h-5 bg-gradient-to-r from-red-500 to-pink-500 rounded-full flex items-center justify-center animate-bounce-in">
                  <span className="text-white text-[10px] font-bold">{unreadCount}</span>
                </div>
              )}
            </button>
            {showNotifications && (
              <NotificationPanel
                notifications={state.notifications}
                onClose={() => setShowNotifications(false)}
                dispatch={dispatch}
              />
            )}
          </div>

          {/* Settings */}
          <button
            onClick={() => dispatch({ type: 'SET_ACTIVE_TAB', payload: 'settings' })}
            className="p-2 rounded-xl hover:bg-white/50 transition-all"
            aria-label="Settings"
          >
            <Settings className="h-5 w-5 text-gray-600" />
          </button>

          {/* Staff avatar */}
          <button
            onClick={() => dispatch({ type: 'SET_ACTIVE_TAB', payload: 'settings' })}
            className="w-9 h-9 bg-gradient-to-br from-brand-400 to-accent-500 text-white rounded-xl flex items-center justify-center hover:shadow-brand transition-all duration-200 hover:scale-105"
          >
            <span className="font-semibold text-xs">
              {state.staff.name.split(' ').map(n => n[0]).join('')}
            </span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default Header;
